<template>
    <p v-if="activities.length === 0" class="py-6 text-center text-sm text-muted">
        {{ $t('activity.empty') }}
    </p>

    <ol v-else class="flex flex-col">
        <li
            v-for="(item, index) in activities"
            :key="item.id"
            class="relative flex gap-3 pb-4 last:pb-0"
        >
            <!-- The thread that joins the lines of one history. -->
            <span
                v-if="index < activities.length - 1"
                class="absolute left-3.5 top-8 -bottom-0 w-px bg-accented"
                aria-hidden="true"
            />

            <span class="relative flex size-7 shrink-0 items-center justify-center rounded-full bg-elevated text-muted ring-4 ring-(--ui-bg)">
                <UIcon :name="activityIcon(item.kind)" class="size-3.5" />
            </span>

            <div class="min-w-0 flex-1 pt-0.5">
                <p class="text-sm text-default">
                    <span class="font-medium text-highlighted">{{ item.actor?.name ?? $t('activity.system') }}</span>
                    <span class="text-muted" :title="formatDateTime(item.at)"> · {{ agoLabel(item.at) }}</span>
                </p>

                <p class="mt-0.5 text-sm text-toned break-words">{{ activitySentence(item) }}</p>

                <div v-if="item.task || showProject" class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <button
                        v-if="item.task && activityTaskOpenable(item)"
                        type="button"
                        class="max-w-full truncate rounded-sm text-left font-medium text-primary hover:underline"
                        @click="taskDrawer.open(item.task.id)"
                    >
                        {{ item.task.key }} {{ item.task.title }}
                    </button>
                    <span v-else-if="item.task" class="max-w-full truncate text-muted line-through">
                        {{ item.task.key }} {{ item.task.title }}
                    </span>

                    <RouterLink
                        v-if="showProject && item.project"
                        :to="{ name: 'project', params: { id: item.project.id } }"
                        class="truncate text-dimmed hover:text-default hover:underline"
                    >
                        {{ item.project.key }} · {{ item.project.name }}
                    </RouterLink>
                </div>
            </div>

            <span v-if="!compact" class="hidden shrink-0 pt-0.5 text-xs text-dimmed sm:block">
                {{ formatDateTime(item.at) }}
            </span>
        </li>
    </ol>
</template>

<script lang="ts" setup>
    import { RouterLink } from 'vue-router'

    import { activityIcon, activitySentence, activityTaskOpenable } from '@/libs/activity'
    import { agoLabel, formatDateTime } from '@/libs/dayjs'
    import { useTaskDrawerStore } from '@/stores/taskDrawer'
    import type { IActivity } from '@/types/Activity'

    /*
     * A history, newest first: who, when, what — and the task, which opens in the drawer.
     * The project is named only where the list mixes projects (the dashboard); inside a
     * project or a task it would say the same word on every line. `compact` is for a narrow
     * place (a side column, the drawer): the exact time moves into the hover text.
     */
    withDefaults(defineProps<{ activities: IActivity[], showProject?: boolean, compact?: boolean }>(), {
        showProject: false,
        compact:     false,
    })

    const taskDrawer = useTaskDrawerStore()
</script>
